/* eslint-disable eqeqeq */
/* eslint-disable no-useless-computed-key */
import { makeStyles } from '@mui/styles';
import Badge from 'apollo-react/components/Badge';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Modal from 'apollo-react/components/Modal';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import Table from 'apollo-react/components/Table';
import TextField from 'apollo-react/components/TextField';
import EllipsisHorizontal from 'apollo-react-icons/EllipsisHorizontal';
import Search from 'apollo-react-icons/Search';
import Trash from 'apollo-react-icons/Trash';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionsMappingModal from './ActionsMappingModal';
import { INPUT_ICON_DEFAULT_SIZE } from '../../../../../constants/CommonStylesConstants';
import { useFormSelector, useTableLastRowVisibilityEffect } from '../../../../../custom-hooks';
import { getUITA } from '../../../../../utils/commonUtils';
import {
  ALL_SUPPORTED_UPDATE_ACTIONS_TYPES,
  CONTROL_TYPES_WITH_INTERACTION_ACTION,
  ENTITY_MODEL_EDITOR,
  EXPRESSION_EDITOR,
  INTERACTION_MODEL_EDITOR,
  INTERACTION_TYPE_FIELD,
  OPERATION_TYPE_FIELD_DICTIONARY,
} from '../../../../constants/editor.constants';
import { SOURCE_TYPES } from '../../../../constants/form-manager.constants';
import {
  ACTION_TYPE_SET_DEFAULT,
  INTERACTION_RULES_SUPPORTED_TYPES,
} from '../../../../constants/runtime.constants';
import {
  getDataSourcesList,
  getEntityDefinition,
  getTranslations,
} from '../../../../selectors/common.selectors';
import { getDummyParameter, getParameterType } from '../../../../utils/actions/rule.actions.utils';
import { processActions } from '../../../../utils/editor.modal-actions.utils';
import {
  columnTextToFormula,
  columnValueToFormula,
  getDatasourceFields,
  getDataSourceSelectValues,
  getSelectedSourceName,
} from '../../../../utils/editor.utils';
import { getPropTranslation, updateRulesScripts } from '../../../../utils/form-manager.utils';
import ConditionModal from '../ConditionModal/ConditionModal';
import TablePropertiesModal from '../TablePropertiesModal';

const SUPPORTED_SOURCE_TYPES = _.mapKeys(
  _.pick(SOURCE_TYPES, ['DATASOURCE', 'FORMULA']),
  (_, key) => key.toLowerCase()
);

const getTargetValue = ({
  dummyTarget,
  targetMapping,
  parameterType,
  type,
  parameter,
  dummyParameter,
} = {}) => {
  const isInteractionRule = INTERACTION_RULES_SUPPORTED_TYPES.includes(type);

  if (!isInteractionRule) {
    return dummyTarget ? `${dummyTarget} (${targetMapping.split('.')[1]})` : '';
  }
  return parameterType === 'table' ? `${dummyParameter} (${parameter})` : parameter;
};

const useStyles = makeStyles({
  root: {
    overflowY: 'auto',
    maxHeight: '600px',

    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },

    '& .selectedRow a, .selectedRow svg, .selectedRow button': {
      color: 'white',
    },

    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },

    '& [class*="MuiToolbar-root"]': {
      padding: '10px 0 0 0 !important',
    },

    '& [class*="MuiTable-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },

    '& [class*="middleAlign"]': {
      height: '60px !important',
    },

    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },

    '& th[class*="InternalTableHeader-resizableHeader"] ': {
      resize: 'none',
    },
  },

  customHeader: {
    justifyContent: 'unset !important',
    alignItems: 'center !important',
    width: '100%',

    '& div': {
      paddingRight: '15px',
      float: 'left',
    },

    '& div:last-child': {
      flex: 1,
      float: 'right',
    },
  },

  titleHeader: {
    fontSize: 18,
    color: '#000000',
    position: 'relative',
    margin: '0 0 0 10px',
  },

  modal: {
    minWidth: '70%',
  },

  dataModelModal: {
    minWidth: 700,
    minHeight: '70%',
    '& [ui-ta*="DataModel section"]': {
      height: '100%',
    },
  },

  badge: {
    overflow: 'visible !important',
  },

  expressionModalContainer: {
    minWidth: 700,
  },
});

const fieldStyles = {
  style: {
    marginTop: 3,
    marginLeft: -8,
  },
};

const ActionCell = ({ row: { id, onDelete } }) => (
  <IconButton onClick={() => onDelete(id)}>
    <Trash />
  </IconButton>
);

const TargetCell = ({ row, column: { accessor: key } }) => {
  const { dummyTarget, parameter, handleOpenTablePropertiesModal } = row;
  const [value, setValue] = useState(getTargetValue(row));

  useEffect(() => {
    const targetValue = getTargetValue(row);
    setValue(targetValue ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dummyTarget, parameter]);

  return (
    <TextField
      ui-ta={getUITA('Select Target')}
      size="small"
      fullWidth
      icon={
        <Search
          ui-ta={getUITA('Target search icon')}
          style={INPUT_ICON_DEFAULT_SIZE}
          color="primary"
          onClick={() => handleOpenTablePropertiesModal(key, row)}
        />
      }
      value={value}
      {...fieldStyles}
    />
  );
};

const SourceCell = ({ row, column: { accessor: key } }) => {
  const { sourceType, dataSourceSelectValues, handleOpenTablePropertiesModal, type } = row;
  const [value, setValue] = useState(() => getSelectedSourceName(row, key, dataSourceSelectValues));
  const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(type);

  useEffect(() => {
    setValue(getSelectedSourceName(row, key, dataSourceSelectValues) ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row[key]]);

  switch (sourceType) {
    case SOURCE_TYPES.DATASOURCE:
      return (
        <Select
          fullWidth
          disabled={!!isInteractionAction}
          canDeselect={false}
          value={value}
          onChange={({ target }) => {
            row.editRow(row.id, key, target.value);
          }}
          {...fieldStyles}
        >
          {dataSourceSelectValues.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      );

    default:
      return (
        <TextField
          ui-ta={getUITA('Select Source')}
          disabled={!!isInteractionAction}
          size="small"
          fullWidth
          icon={
            <Search
              style={INPUT_ICON_DEFAULT_SIZE}
              color="primary"
              onClick={() => handleOpenTablePropertiesModal(key, row)}
            />
          }
          value={value}
          onChange={({ target }) => {
            setValue(target.value);
          }}
          onBlur={() => {
            row.editRow(row.id, key, value);
          }}
          {...fieldStyles}
        />
      );
  }
};

const MappingCell = ({ row }) => {
  const classes = useStyles();

  const { sourceType, handleOpenMappingModal, mapping, type } = row;
  const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(type);

  return sourceType === SOURCE_TYPES.DATASOURCE ? (
    <Badge
      badgeContent={Array.isArray(mapping) ? mapping.length : 0}
      ui-ta={getUITA('Update Actions Mapping', 'Count')}
    >
      <IconButton
        className={classes.badge}
        disabled={!!isInteractionAction}
        variant="primary"
        onClick={() => handleOpenMappingModal(row)}
        ui-ta={getUITA('Update Actions Mapping', 'Open Btn')}
      >
        <EllipsisHorizontal />
      </IconButton>
    </Badge>
  ) : (
    ''
  );
};

const makeEditableSelectCell = (options, isSourceTypeColumn) => ({
  row,
  column: { accessor: key },
}) => {
  const sourceType =
    row[key] === SOURCE_TYPES.DATASOURCE ? SOURCE_TYPES.DATASOURCE : SOURCE_TYPES.FORMULA;
  const value = isSourceTypeColumn ? sourceType : row[key];
  const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(row.type);

  return (
    <Select
      ui-ta={getUITA(`Select${key}`)}
      fullWidth
      canDeselect={false}
      disabled={!!(isInteractionAction && isSourceTypeColumn)}
      value={value}
      onChange={({ target }) => {
        row.editRow(row.id, key, target.value);
      }}
      {...fieldStyles}
    >
      {Object.entries(options).map(([_key, value]) => (
        <MenuItem ui-ta={getUITA(`listBox ${value}`)} key={_key} value={_key}>
          {value}
        </MenuItem>
      ))}
    </Select>
  );
};

const FilterCell = ({ row, column: { accessor: key } }) => {
  const classes = useStyles();

  const { handleOpenFilterModal, type } = row;
  const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(type);

  const filters = row[key]?.Conditions;

  return (
    <Badge badgeContent={Array.isArray(filters) ? filters.length : 0}>
      <IconButton
        className={classes.badge}
        disabled={!!isInteractionAction}
        variant="primary"
        onClick={() => handleOpenFilterModal(key, row)}
      >
        <EllipsisHorizontal />
      </IconButton>
    </Badge>
  );
};

const TypeCell = (currentControlModel) => {
  if (CONTROL_TYPES_WITH_INTERACTION_ACTION.includes(currentControlModel.controlType)) {
    return makeEditableSelectCell(INTERACTION_TYPE_FIELD);
  } else {
    return makeEditableSelectCell(OPERATION_TYPE_FIELD_DICTIONARY);
  }
};

const normalizeActions = (entityDefinition, translations) => ({
  dummySource,
  source,
  sourceMapping,
  sourceType,
  ruleActionType,
  conditionMet,
  ...rest
}) => ({
  ...rest,
  sourceType,
  ruleActionType,
  dummySource:
    ruleActionType === 'interaction'
      ? ''
      : sourceType === SOURCE_TYPES.TEXT
      ? columnTextToFormula(dummySource)
      : sourceType === SOURCE_TYPES.COLUMN
      ? columnValueToFormula(entityDefinition, translations, sourceMapping)
      : updateRulesScripts(dummySource, true),
  sourceMapping: ruleActionType === 'interaction' ? '' : updateRulesScripts(sourceMapping, true),
  source: ruleActionType === 'interaction' ? '' : updateRulesScripts(source, true),
  conditionMet: _.isNil(conditionMet) ? true : conditionMet,
});

const ActionsModal = (props) => {
  const classes = useStyles();

  const { name, ruleId, currentControlModel, setCurrentControlModel } = props;

  const controllerUpdateValue = currentControlModel[name];
  const tempPropValue = controllerUpdateValue?.rules;
  const propValue = tempPropValue ? tempPropValue.find((item) => item.id === ruleId) : [];
  const { actions, entityName } = propValue && propValue.actions ? propValue : [];

  const entityDefinition = useFormSelector(getEntityDefinition);
  const translations = useSelector(getTranslations);
  const dataSourceList = useFormSelector(getDataSourcesList);

  const currentKey = useRef('');
  const [rows, setRows] = useState([]);
  const [valueRadio, setValueRadio] = useState('1');

  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);

  const [selectedMapping, setSelectedMapping] = useState([]);
  const [selectedFilterType, setSelectedFilterType] = useState('');
  const [selectedRow, setSelectedRow] = useState({});

  const tableRef = useRef();

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const actionsArray = actions?.map(normalizeActions(entityDefinition, translations)) ?? [];
  const dataSourceSelectValues = getDataSourceSelectValues(dataSourceList);
  const selectedDataSource = dataSourceList.find(
    ({ dataSourceKey }) =>
      dataSourceKey === selectedRow.dummySource || dataSourceKey === selectedRow.source
  );

  useEffect(() => {
    setRows(actionsArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId]);

  const columns = [
    {
      header: 'Type',
      accessor: 'type',
      customCell: TypeCell(currentControlModel),
    },
    {
      header: 'Target',
      accessor: 'dummyTarget',
      customCell: TargetCell,
    },
    {
      header: 'Target Filter',
      accessor: 'targetFilter',
      customCell: FilterCell,
    },
    {
      header: 'Source Type',
      accessor: 'sourceType',
      customCell: makeEditableSelectCell(SUPPORTED_SOURCE_TYPES, true),
    },
    {
      header: 'Source',
      accessor: 'dummySource',
      customCell: SourceCell,
    },
    {
      header: 'Source Filter',
      accessor: 'sourceFilter',
      customCell: FilterCell,
    },
    {
      header: 'Mapping',
      accessor: 'mapping',
      customCell: MappingCell,
    },
    {
      accessor: 'actiondelete',
      customCell: ActionCell,
    },
  ];

  const handleOpenMappingModal = (row) => {
    setSelectedMapping(row.mapping || []);
    setSelectedRow(row);
    setIsMappingModalOpen(true);
  };

  const handleCloseMappingModal = () => setIsMappingModalOpen(false);

  const handleSaveMapping = () => {
    editRow(selectedRow.id, 'mapping', selectedMapping.length ? selectedMapping : '');
    handleCloseMappingModal();
  };

  const handleOpenFilterModal = (key, row) => {
    setSelectedFilterType(key);
    setSelectedRow(row);
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => setIsFilterModalOpen(false);

  const handleOpenTablePropertiesModal = (key, row) => {
    currentKey.current = key;
    setIsTablePropertiesModalOpen(true);
    setSelectedRow({ ...row, currentKey: key });
  };

  const handleSaveFilter = () => {
    editRow(selectedRow.id, selectedFilterType, selectedRow[selectedFilterType]);
    setIsFilterModalOpen(false);
  };

  const updateLocalState = (newRows) => {
    const processedRows = processActions({
      rows: newRows,
      ruleActionType: name,
      entityName,
      entityDefinition,
    });
    const elementsIndex = controllerUpdateValue.rules.findIndex((element) => element.id === ruleId);
    const newValue = { ...controllerUpdateValue };

    newValue.rules[elementsIndex] = {
      ...newValue.rules[elementsIndex],
      type: name,
      actions: processedRows,
    };

    setRows(processedRows);
    setCurrentControlModel((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleChange = ({ target }) => {
    setValueRadio(target.value);
  };

  const onDelete = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    updateLocalState(newRows);
  };

  const editRow = (id, key, value) => {
    const getUpdatedInteractionAction = (row, key, value) => {
      const dummyTarget = key && key.includes('dummyTarget');

      if (dummyTarget) {
        key = 'parameter';
      }

      const updatedRow = {
        ...row,
        [key]: key === 'parameter' ? value.value : value,
        ...(key === 'parameter' && {
          ['dummyParameter']: getDummyParameter({ key, targetDetails: value, translations }),
        }),
        ...(key === 'parameter' && {
          ['parameterType']: getParameterType({ key, targetDetails: value }),
        }),
      };

      return updatedRow;
    };

    const getUpdatedUpdateAction = (row, key, value) => {
      const dummyTarget = key && key.includes('dummyTarget');
      const newValue = dummyTarget
        ? getPropTranslation(entityDefinition, translations, value)
        : value;

      const updatedRow = {
        ...row,
        [key]: value,
        ...(dummyTarget && { ['dummyTarget']: newValue }),
        ...(dummyTarget && { ['targetMapping']: value ?? '' }),
        ...(dummyTarget && { ['target']: value ? value.split('.')[0] : entityName }),
      };

      return updatedRow;
    };

    const getUpdatedRows = (rows = [], key, value) => {
      return rows.map((row) => {
        if (row.id !== id) {
          return row;
        }

        if (key === 'type' && row.type !== value) {
          row = {
            id,
            mapping: '',
            type: value,
            dummyType: '',
            ruleActionType: '',
            conditionMet: valueRadio == '1',
            sourceType: SOURCE_TYPES.FORMULA,
            source: '',
            sourceFilter: '',
            dummySource: '',
            sourceMapping: '',
            targetType: '',
            target: '',
            targetMapping: '',
            targetDataType: '',
            targetFilter: '',
            dummyTarget: '',
            templateName: '',
            value: '',
          };
          return row;
        }

        return INTERACTION_RULES_SUPPORTED_TYPES.includes(row.type)
          ? getUpdatedInteractionAction(row, key, value)
          : getUpdatedUpdateAction(row, key, value);
      });
    };

    let newRows = getUpdatedRows(rows, key, value);

    if (key === 'sourceType') {
      newRows = newRows.map((row) =>
        row.id === id
          ? {
              ...row,
              mapping: '',
              source: '',
              sourceFilter: '',
              dummySource: '',
            }
          : row
      );
    }

    setRows(newRows);
    updateLocalState(newRows);
  };

  const addRow = () => {
    const rowId = uuidv4();
    const newAction = {
      id: rowId,
      mapping: '',
      type: ACTION_TYPE_SET_DEFAULT,
      dummyType: '',
      ruleActionType: '',
      conditionMet: valueRadio == '1',
      sourceType: SOURCE_TYPES.FORMULA,
      source: '',
      sourceFilter: '',
      dummySource: '',
      sourceMapping: '',
      targetType: '',
      target: '',
      targetMapping: '',
      targetDataType: '',
      targetFilter: '',
      dummyTarget: '',
      templateName: '',
      value: '',
    };
    const newRows = [...rows, newAction];
    updateLocalState(newRows);
  };

  const CustomHeader = () => (
    <div className={classes.customHeader}>
      <div>
        <h3 className={classes.titleHeader}>{'Actions'}</h3>
      </div>
      <div>
        <Button variant="text" onMouseDown={addRow} ui-ta={getUITA('ModalButton Add Action')}>
          {'Add Action'}
        </Button>
      </div>
      <div>
        <RadioGroup value={valueRadio} onChange={handleChange} row>
          <Radio value="1" label="When condition met" />
          <Radio value="0" label="When condition not met" />
        </RadioGroup>
      </div>
    </div>
  );

  const getModalType = (key) => {
    const isInteractionAction = INTERACTION_RULES_SUPPORTED_TYPES.includes(selectedRow.type);

    switch (key) {
      case 'dummyTarget':
        if (isInteractionAction) {
          return INTERACTION_MODEL_EDITOR;
        } else {
          return ENTITY_MODEL_EDITOR;
        }
      default:
        return EXPRESSION_EDITOR;
    }
  };

  return (
    <>
      <div className={classes.root} ref={tableRef}>
        <Table
          columns={columns.map((column) => ({ ...column, fixedWidth: false }))}
          rows={rows
            .filter(
              ({ conditionMet, type }) =>
                (conditionMet == (valueRadio == '1') &&
                  ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(type)) ||
                !type
            )
            .map((row) => ({
              ...row,
              key: row.id,
              currentKey: currentKey.current,
              dataSourceSelectValues,
              onDelete,
              editRow,
              classes,
              handleOpenMappingModal,
              handleOpenFilterModal,
              handleOpenTablePropertiesModal,
            }))}
          initialSortOrder="asc"
          hidePagination
          CustomHeader={CustomHeader}
        />
      </div>
      <Modal
        open={isMappingModalOpen}
        variant="default"
        className={classes.modal}
        onClose={handleCloseMappingModal}
        title="Mapping Editor (Data Mapping)"
        buttonProps={[
          {
            label: 'Cancel',
            onClick: handleCloseMappingModal,
            'ui-ta': getUITA('Update Actions Mapping', 'Cancel Btn'),
          },
          {
            label: 'Save',
            onClick: handleSaveMapping,
            'ui-ta': getUITA('Update Actions Mapping', 'Save Btn'),
          },
        ]}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ActionsMappingModal
          rows={selectedMapping}
          setRows={setSelectedMapping}
          selectedTarget={selectedRow.target}
          selectedDataSource={selectedDataSource}
          entityDefinition={entityDefinition}
          translations={translations}
        />
      </Modal>
      <Modal
        open={isFilterModalOpen}
        variant="default"
        className={classes.modal}
        onClose={handleCloseFilterModal}
        title={`${selectedFilterType === 'targetFilter' ? 'Target' : 'Source'} Filter`}
        buttonProps={[
          {
            label: 'Cancel',
            onClick: handleCloseFilterModal,
          },
          {
            label: 'Save',
            onClick: handleSaveFilter,
          },
        ]}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ConditionModal
          name={selectedFilterType}
          isDataSource={
            selectedFilterType === 'sourceFilter' &&
            selectedRow.sourceType === SOURCE_TYPES.DATASOURCE
          }
          currentControlModel={selectedRow}
          setCurrentControlModel={setSelectedRow}
          dataSourceFilterColumns={getDatasourceFields(selectedDataSource)}
        />
      </Modal>

      {isTablePropertiesModalOpen && (
        <TablePropertiesModal
          type={getModalType(selectedRow.currentKey)}
          row={selectedRow}
          handleUpdate={(id, key, value) => {
            editRow(id, key, value);
          }}
          onClose={() => {
            setIsTablePropertiesModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default React.memo(ActionsModal);
